import { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Array as A, Option, pipe } from 'effect';
import { isPolygonInArea } from '@/domain/journal/helper';
import {
  ocrBoundingBox,
  type DisplayedOcrPolygon,
  type DisplayedOcrPolygonRect,
  type OcrBoundingPoly,
  type OcrBox,
} from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageOcrLayer/ocrGeometry';
import {
  useRegisterOcrWordsSelectionGesture,
  type DisplayedPagePoint,
  type OcrWordsSelectionGesture,
} from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageOcrLayer/OcrWords/ocrWordsSelectionGesture';

export type OcrWordsSelection = { text: string; box: DisplayedOcrPolygonRect; ocrBoundingPoly: OcrBoundingPoly };

type Drawing = { from: DisplayedPagePoint; selected: ReadonlyArray<number> };

const rectBetween = (from: DisplayedPagePoint, to: DisplayedPagePoint): DisplayedOcrPolygonRect => ({
  left: Math.min(from.x, to.x),
  top: Math.min(from.y, to.y),
  width: Math.abs(to.x - from.x),
  height: Math.abs(to.y - from.y),
});

const boundingRect = (rects: A.NonEmptyReadonlyArray<DisplayedOcrPolygonRect>): DisplayedOcrPolygonRect => {
  const left = Math.min(...A.map(rects, (rect) => rect.left));
  const top = Math.min(...A.map(rects, (rect) => rect.top));
  const right = Math.max(...A.map(rects, (rect) => rect.left + rect.width));
  const bottom = Math.max(...A.map(rects, (rect) => rect.top + rect.height));
  return { left, top, width: right - left, height: bottom - top };
};

export const useOcrWordsSelection = (
  words: ReadonlyArray<OcrBox>,
  displayedWords: ReadonlyArray<DisplayedOcrPolygon>,
) => {
  const color = useTheme().palette.primary.main;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Drawn on the canvas on every move without a render; React state changes only when the selection is made.
  const drawing = useRef<Drawing | null>(null);
  const [selection, setSelection] = useState<OcrWordsSelection | null>(null);

  const gesture = useMemo<OcrWordsSelectionGesture>(() => {
    const context = () => canvasRef.current?.getContext('2d') ?? null;
    const clearCanvas = () => {
      const canvas = canvasRef.current;
      context()?.clearRect(0, 0, canvas?.width ?? 0, canvas?.height ?? 0);
    };
    const outline = (rects: ReadonlyArray<DisplayedOcrPolygonRect>) => {
      const ctx = context();
      if (!ctx) return;
      ctx.strokeStyle = color;
      rects.forEach(({ left, top, width, height }) => ctx.strokeRect(left, top, width, height));
    };
    const clear = () => {
      drawing.current = null;
      clearCanvas();
      setSelection(null);
    };

    return {
      start: (point) => {
        drawing.current = { from: point, selected: [] };
      },
      move: (point) => {
        if (!drawing.current) return;
        const area = rectBetween(drawing.current.from, point);
        const selected = pipe(
          displayedWords,
          A.filterMap((word, index) =>
            isPolygonInArea({ x: area.left, y: area.top, width: area.width, height: area.height }, word)
              ? Option.some(index)
              : Option.none(),
          ),
        );
        drawing.current = { ...drawing.current, selected };
        clearCanvas();
        outline([area, ...A.map(selected, (index) => displayedWords[index])]);
      },
      end: () => {
        if (!drawing.current) return;
        const { selected } = drawing.current;
        drawing.current = null;
        clearCanvas();
        if (!A.isNonEmptyReadonlyArray(selected)) {
          setSelection(null);
          return;
        }
        const selectedWords = A.map(selected, (index) => displayedWords[index]);
        outline(selectedWords);
        setSelection({
          text: A.map(selectedWords, (word) => word.text).join(' '),
          box: boundingRect(selectedWords),
          // the selected indexes count the page's OCR words in their OCR order
          ocrBoundingPoly: ocrBoundingBox(A.map(selected, (index) => words[index])),
        });
      },
      clear,
    };
  }, [color, words, displayedWords]);

  // displayedWords keeps its reference until the words or the page layout change; a selection drawn before is gone.
  useEffect(() => gesture.clear, [gesture]);

  useRegisterOcrWordsSelectionGesture(gesture);

  return { canvasRef, selection, gesture };
};
